<template>
  <b-container>
    <b-row class="justify-content-center">
      <b-col md="10" v-if="item">
        <b-card class="mb-3 item-detail">
          <!-- YT Preview -->
          <div v-if="youtubeId" class="mb-8">
            <youtube :video-id="youtubeId"></youtube>
          </div>
          <div v-if="videoUrl" class="mb-8">
            <b-embed
              type="video"
              aspect="16by9"
              controls
              :src="videoUrl"
            ></b-embed>
          </div>
          <div v-if="gdriveUrl" class="mb-8">
            <b-embed type="iframe" aspect="16by9" :src="gdriveUrl"></b-embed>
          </div>
          <div class="display-4 mb-8">{{ item.title }}</div>
          <b-badge class="mb-8" variant="highlight">{{
            itemTypeTitle(item.item_type)
          }}</b-badge>
          <p>{{ item.description }}</p>
          <ul class="list-inline" v-if="item.skills && item.skills.length > 0">
            <li
              class="list-inline-item"
              v-for="skill in item.skills"
              :key="skill.skill_id"
            >
              <b-badge variant="light">
                {{ skill.title.split(' ')[0] }}
              </b-badge>
            </li>
          </ul>
          <router-link :to="`/user/${item.owner.uuid}`">
            <img
              class="rounded-circle img-profile mr-2"
              :src="item.owner.avatar"
              :alt="`${item.owner.first_name} ${item.owner.last_name}`"
            />
            {{ item.owner.first_name }} {{ item.owner.last_name }}
          </router-link>
          <div v-if="showAction">
            <hr />
            <b-btn variant="primary" size="sm" @click="actionButtonTapped()">
              {{ actionText(item.item_type) }}
            </b-btn>
          </div>
          <b-row class="mt-3">
            <b-col cols="12" md="6">
              <!-- Review entry area -->
              <b-form @submit.prevent="submit">
                <h3 class="text-left">{{ hasReviewed ? "Your rating" : "Rate this resource?" }}</h3>
                <div class="d-flex justify-content-center mb-3">
                  <star-rating
                    :show-rating="false"
                    :increment="0.5"
                    v-model="rating"
                  />
                </div>

                <hr />

                <h3 class="text-left">{{ hasReviewed ? "Your review" : "Review this resource?" }}</h3>
                <b-form-group>
                  <b-form-textarea
                    class="mb-16"
                    rows="3"
                    v-model="notes"
                    :disabled="loading"
                  />
                </b-form-group>

                <b-btn
                  type="submit"
                  :disabled="loading"
                  variant="secondary"
                  size="lg"
                  block
                >
                  {{ hasReviewed ? "Update" : "Submit" }} Review
                </b-btn>
              </b-form>
            </b-col>
            <b-col cols="12" md="6" class="text-left">
              <h2 class="mb-0">{{item.avg_rating}}/5</h2>
              <h3>{{item.review_count}} Rating<span v-if="item.review_count != 1">s</span></h3>
              <educational-item-review-list
                :uuid="uuid"
                :fromAdmin="true"
                class="review-list"
              ></educational-item-review-list>
            </b-col>
          </b-row>
        </b-card>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import EducationalItemReviewList from './EducationalItemReviewList.vue'
import StarRating from 'vue-star-rating'

export default {
  components: { EducationalItemReviewList, StarRating },
  name: 'EducationalItemDetail',
  props: {
    uuid: String,
  },
  data() {
    return {
      item: false,
      youtubeId: false,
      videoUrl: false,
      gdriveUrl: false,
      showAction: false,
      errors: {},
      loading: false,
      rating: 0,
      notes: '',
      hasReviewed: false
    }
  },
  mounted() {
    this.loadItem()
  },
  methods: {
    /**
     * Loads the item from the api
     */
    loadItem() {
      this.loading = true
      this.errors = {}

      this.$http
        .get(`/resources/${this.uuid}`)
        .then(
          (res) => {
            this.item = res.data.data
            this.handleItemResponse()
          },
          (err) => {
            this.errors = err.response.data.errors
          }
        )
        .finally(() => {
          this.loading = false
        })
    },
    /**
     * Updates UI based properties to show/hide the correct actions
     */
    handleItemResponse() {
      if (!this.item) {
        return
      }
      // Check to see if actions should be shown
      switch (this.item.item_type) {
        case 'pdf':
        case 'url':
        case 'ppt':
        case 'xls':
          this.showAction = true
          break
        case 'mp4':
          // Set up video embed
          this.videoUrl = this.item.url
          break
        case 'youtube':
          // Set up yt embed
          this.youtubeId = this.$youtube.getIdFromURL(this.item.url)
          break
        case 'gdrive':
          this.gdriveUrl = this.item.url.split('/view')[0] + '/preview'
          break
        default:
          break
      }

      // Track the viewing if the action button is not required. Otherwise those resources will never be tracked
      if (!this.showAction) {
        this.$http.post(`/resources/${this.item.uuid}/trackView`).then(() => {})
      }

      let userReview = this.item.user_review
      if (userReview) {
        this.hasReviewed = true
        this.rating = userReview.rating
        this.notes = userReview.notes
      }
    },

    /** Checks to see if the user has previously left a review for this resource. If they have, use it as update text in the form on the left */
    checkUserReviewStatus() {
      if (this.item && this.item.reviews && this.item.reviews.length > 0) {
        let filtered = this.item.reviews.filter(r => r.user.uuid === this.$auth.user().uuid)
        if (filtered.length == 0) { 
          this.hasReviewed = false
          return
        }
        let review = filtered[0]
        this.hasReviewed = true
        this.rating = review.rating
        this.notes = review.notes
      } else {
        this.hasReviewed = false
      }
    },
    /**
     * Returns a more human readable title for EducationalResourceItemTypes
     */
    itemTypeTitle(itemType) {
      switch (itemType) {
        case 'pdf':
        case 'url':
        case 'mp4':
          return itemType.toUpperCase()
        case 'ppt':
          return 'Power Point'
        case 'xls':
          return 'Excel'
        case 'youtube':
          return 'YouTube'
        case 'gdrive':
          return 'Google Drive'
        default:
          return ''
      }
    },
    /**
     * Returns the text to be displayed in the action button
     */
    actionText(itemType) {
      switch (itemType) {
        case 'pdf':
        case 'ppt':
        case 'mp4':
        case 'xls':
          return 'Download'
        case 'youtube':
        case 'gdrive':
        case 'url':
          return 'Open'
        default:
          return ''
      }
    },

    /**
     * Opens up the resource in a new tab. Only used for pdf, url and ppt types currently
     */
    actionButtonTapped() {
      this.loading = true

      this.$http
        .post(`/resources/${this.item.uuid}/trackView`)
        .then(() => {
          window.open(this.item.url, '_blank')
        })
        .finally(() => {
          this.loading = false
        })
    },

    /**
     * Submits the currently typed review to the server. Should only be possible if the user hasn't made a review of this previously. Will need to handle editing later
     */
    submit() {
      this.loading = true
      this.$http
        .post(`/resources/${this.item.uuid}/review`, {
          rating: this.rating,
          notes: this.notes,
        })
        .then(() => {
          this.hasReviewed = true
          this.loadItem()
        })
        .finally(() => {
          this.loading = false
        })
    },
  },
}
</script>

<style scoped>
.item-detail {
  text-align: center;
  font-size: 0.875rem;
}
.img-profile {
  width: 24px;
  height: 24px;
}
.review-list {
  max-height: 400px;
}
</style>
